import { ParameterType } from '@/study-search/enums';
import { BotHelper } from '@/study-search/utils/BotHelper';
import { LocationQueryRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { DisciplineFilterDefinition } from '../models/definition';
import { DisciplineFilterOptionInformation } from '@studyportals/search-filters';
import store from '@/study-search/store';

class RoutingDefinitions {
	public readonly ROOT = 'Root';
	public readonly PORTAL_ROUTE = 'Portal';
	public readonly DISCIPLINE_DEGREE_TYPE_ROUTE = 'DisciplineDegreeType';
	public readonly DISCIPLINE_DELIVERY_METHOD_ROUTE = 'DisciplineDeliveryMethod';
	public readonly DISCIPLINE_DURATION_ROUTE = 'DisciplineDuration';
	public readonly DISCIPLINE_FORMAT_ROUTE = 'DisciplineAttendance';
	public readonly DISCIPLINE_TUITION_FEE_ROUTE = 'DisciplineTuitionFee';
	public readonly DISCIPLINE_TUITION_FEE_DELIVERY_METHOD_ROUTE = 'DisciplineTuitionFeeDeliveryMethod';
	public readonly DISCIPLINE_ROUTE = 'Discipline';
	public readonly DISCIPLINE_COUNTRY_ROUTE = 'DisciplineCountry';
	public readonly DISCIPLINE_EDUCATIONAL_FORM_ROUTE = 'DisciplineEducationalForm';
	public readonly DISCIPLINE_EDUCATIONAL_FORM_COUNTRY_ROUTE = 'DisciplineEducationalFormCountry';
	public readonly COUNTRY_DISCIPLINE_ROUTE = 'CountryDiscipline';
	public readonly DISCIPLINE_COUNTRY_AREA_ROUTE = 'DisciplineCountryArea';
	public readonly DISCIPLINE_AREA_ROUTE = 'DisciplineArea';
	public readonly DISCIPLINE_CONTINENT_ROUTE = 'DisciplineContinent';
	public readonly DISCIPLINE_DEGREE_TYPE_COUNTRY_ROUTE = 'DisciplineDegreeTypeCountry';
	public readonly COUNTRY_ROUTE = 'Country';
	public readonly AREA_ROUTE = 'Area';
	public readonly CONTINENT_ROUTE = 'Continent';
	public readonly COUNTRY_DEGREE_TYPE_ROUTE = 'CountryDegreeType';
	public readonly COUNTRY_ATTENDANCE_DEGREE_TYPE_ROUTE = 'CountryAttendanceDegreeType';
	public readonly COUNTRY_DEGREE_TYPE_DURATION_ROUTE = 'CountryDegreeTypeDuration';
	public readonly COUNTRY_DURATION_ROUTE = 'CountryDuration';
	public readonly COUNTRY_ATTENDANCE_ROUTE = 'CountryAttendance';
	public readonly COUNTRY_FORMAT_ROUTE = 'CountryFormat';
	public readonly COUNTRY_TUITION_FEE_ROUTE = 'CountryTuitionFee';
	public readonly COUNTRY_AREA_ROUTE = 'CountryArea';
	public readonly COUNTRY_EDUCATIONAL_FORM_ROUTE = 'CountryEducationalForm';
	public readonly ERROR = 'Error';
	public readonly ATTENDANCE_ROUTE = 'Attendance';
	public readonly ATTENDANCE_DEGREE_TYPE_ROUTE = 'AttendanceDegreeType';
	public readonly SPECIAL_PROGRAMMES_ROUTE = 'SpecialProgrammes';
	public readonly TUITION_FEE_ROUTE = 'TuitionFee';
	public readonly TUITION_FEE_CONTINENT_ROUTE = 'TuitionFeeContinent';

	private readonly botHelper = new BotHelper();

	public stringifyQuery(this: void, query: LocationQueryRaw): string {
		if (Object.keys(query).length > 0) {
			const mappedQueryParameters = Object.keys(query).map((key) => {
				return `${key.toString()}=${query[key]}`;
			});
			const queryString = mappedQueryParameters.join('&');

			return `${queryString}`;
		}

		return '';
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public beforeEnter(
		this: void,
		to: RouteLocationNormalized,
		from: RouteLocationNormalized,
		next: NavigationGuardNext
	): void {
		const portalPath = store.getters.getPortalPath();
		if (to.params.portal_type !== portalPath) {
			next(`/${portalPath}`);
		} else {
			next();
		}
	}

	public beforeEnterWithDisciplineRouting = (
		to: RouteLocationNormalized,
		from: RouteLocationNormalized,
		next: NavigationGuardNext
	): void | boolean => {
		const portalPath = store.getters.getPortalPath();
		if (to.params.portal_type !== portalPath) {
			next(`/${store.getters.getPortalPath()}`);
		} else if (
			this.isSynonym(to.params.discipline as string) &&
			!this.botHelper.isBot() &&
			!this.botHelper.isPrerender()
		) {
			const filterInformation = this.getDisciplineFilterOptionInformation();
			const disciplineOption = filterInformation.find((_) => _.synonym === to.params.discipline);
			next({
				name: to.name?.toString(),
				params: {
					...to.params,
					discipline: disciplineOption?.optionPath
				},
				query: to.query
			});
		} else {
			next();
		}
	};

	public afterEach = (to: RouteLocationNormalized): void => {
		const navigationFailed = to.matched.length === 0;
		if (navigationFailed) {
			return;
		}

		if (
			this.isRoutingDiscipline(to.name as string) &&
			this.isSynonym(to.params.discipline as string) &&
			(this.botHelper.isBot() || this.botHelper.isPrerender())
		) {
			const filterInformation = this.getDisciplineFilterOptionInformation();
			const disciplineOption = filterInformation.find((_) => _.synonym === to.params.discipline);

			const location = to.fullPath.replace(to.params.discipline as string, disciplineOption?.optionPath as string);
			store.mutations.setMetaRefreshInfo(`/search${location}`);
			return;
		}
		store.mutations.setMetaRefreshInfo('');
	};

	private getDisciplineFilterOptionInformation(): DisciplineFilterOptionInformation[] {
		return store.getters.getFilterOptionInformation(ParameterType.DISCIPLINES) as DisciplineFilterOptionInformation[];
	}

	private isSynonym(term: string): boolean {
		const synonyms = DisciplineFilterDefinition.getSynonyms();
		return synonyms.includes(term);
	}

	private isRoutingDiscipline = (term: string): boolean => {
		return term.indexOf(this.DISCIPLINE_ROUTE) !== -1;
	};
}

export default new RoutingDefinitions();
